import { HEALTH_ENDPOINTS } from '../config/apiRouting';

interface ServiceTest {
  service: string;
  displayName: string;
  testEndpoint: string;
  method: 'GET' | 'HEAD' | 'OPTIONS';
  requiresAuth?: boolean;
}

// Define specific test endpoints for each service - using /health endpoints for reliable checks
// All services now have working /health endpoints as of 2025-12-05
const SERVICE_TESTS: ServiceTest[] = [
  {
    service: 'coreData',
    displayName: 'Core Data API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'menuManagement',
    displayName: 'Menu Management API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'stores',
    displayName: 'Stores API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'employees',
    displayName: 'Employees API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'scheduling',
    displayName: 'Scheduling API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'aiImages',
    displayName: 'AI Images API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'taxManagement',
    displayName: 'Tax Management API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'dayparts',
    displayName: 'Dayparts API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'socialMedia',
    displayName: 'Social Media API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'discounts',
    displayName: 'Discounts API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'securityAudit',
    displayName: 'Security Audit API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'categories',
    displayName: 'Categories API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'menuBoards',
    displayName: 'Menu Boards API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'ubiquitiNetwork',
    displayName: 'Network Management API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'messaging',
    displayName: 'Messaging API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'contactForm',
    displayName: 'Contact Form API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'posSync',
    displayName: 'POS Sync API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'tableCharts',
    displayName: 'Table Charts API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'weather',
    displayName: 'Weather API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'waitlist',
    displayName: 'Waitlist API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'payment',
    displayName: 'Payment API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  },
  {
    service: 'aiChat',
    displayName: 'AI Chat API',
    testEndpoint: '/health',
    method: 'GET',
    requiresAuth: false
  }
];

export interface EnhancedHealthStatus {
  service: string;
  displayName: string;
  status: 'healthy' | 'unhealthy' | 'unknown' | 'cors-error';
  responseTime: number;
  statusCode?: number;
  error?: string;
  endpoint: string;
  lastChecked: Date;
}

export interface EnhancedHealthReport {
  timestamp: Date;
  services: EnhancedHealthStatus[];
  summary: {
    total: number;
    healthy: number;
    unhealthy: number;
    unknown: number;
    corsErrors: number;
  };
}

/**
 * Perform an enhanced health check on a specific service
 * Updated 2025-12-05: All services now have working /health endpoints
 */
async function checkServiceHealth(test: ServiceTest): Promise<EnhancedHealthStatus> {
  const startTime = Date.now();

  // Always use Lambda URL directly for /health checks - bypass API Gateway entirely
  // HEALTH_ENDPOINTS contains direct Lambda URLs, not API Gateway routes
  let fullUrl: string;

  try {
    const healthEndpoint = HEALTH_ENDPOINTS[test.service as keyof typeof HEALTH_ENDPOINTS];
    if (!healthEndpoint) {
      throw new Error(`No health endpoint configured for ${test.service}`);
    }
    fullUrl = healthEndpoint;
  } catch (e) {
    return {
      service: test.service,
      displayName: test.displayName,
      status: 'unhealthy',
      responseTime: 0,
      error: 'Service not configured',
      endpoint: test.testEndpoint,
      lastChecked: new Date()
    };
  }

  try {
    const response = await fetch(fullUrl, {
      method: test.method,
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    const responseTime = Date.now() - startTime;

    // For GET requests to /health endpoints
    if (test.method === 'GET') {
      // 200-299 = healthy
      if (response.ok) {
        return {
          service: test.service,
          displayName: test.displayName,
          status: 'healthy',
          responseTime,
          statusCode: response.status,
          endpoint: test.testEndpoint,
          lastChecked: new Date()
        };
      }

      // 401/403 = API is responding but requires auth (still healthy for health check purposes)
      if (response.status === 401 || response.status === 403) {
        return {
          service: test.service,
          displayName: test.displayName,
          status: 'healthy',
          responseTime,
          statusCode: response.status,
          error: 'Requires authentication (API responding)',
          endpoint: test.testEndpoint,
          lastChecked: new Date()
        };
      }

      // 500+ = unhealthy
      return {
        service: test.service,
        displayName: test.displayName,
        status: 'unhealthy',
        responseTime,
        statusCode: response.status,
        error: `HTTP ${response.status}`,
        endpoint: test.testEndpoint,
        lastChecked: new Date()
      };
    }

    // For OPTIONS requests, check CORS headers
    if (test.method === 'OPTIONS') {
      const corsOrigin = response.headers.get('access-control-allow-origin');
      const hasCorsConfig = corsOrigin && (corsOrigin === '*' || corsOrigin.includes(window.location.origin));

      if (!hasCorsConfig) {
        return {
          service: test.service,
          displayName: test.displayName,
          status: 'cors-error',
          responseTime,
          statusCode: response.status,
          error: 'CORS headers missing or misconfigured',
          endpoint: test.testEndpoint,
          lastChecked: new Date()
        };
      }

      return {
        service: test.service,
        displayName: test.displayName,
        status: 'healthy',
        responseTime,
        statusCode: response.status,
        endpoint: test.testEndpoint,
        lastChecked: new Date()
      };
    }

    // For other methods, check status
    return {
      service: test.service,
      displayName: test.displayName,
      status: response.ok ? 'healthy' : 'unhealthy',
      responseTime,
      statusCode: response.status,
      endpoint: test.testEndpoint,
      lastChecked: new Date()
    };
  } catch (error) {
    const responseTime = Date.now() - startTime;
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Check if it's a CORS error (which actually means the API is responding)
    if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      // Try a no-cors request to verify API is up
      try {
        await fetch(fullUrl, {
          method: 'HEAD',
          mode: 'no-cors'
        });
        // If we get here, the API is responding (just with CORS issues)
        return {
          service: test.service,
          displayName: test.displayName,
          status: 'cors-error',
          responseTime,
          error: 'CORS headers missing - API responding but browser blocked',
          endpoint: test.testEndpoint,
          lastChecked: new Date()
        };
      } catch (networkError) {
        // Real network error - API is actually down
        return {
          service: test.service,
          displayName: test.displayName,
          status: 'unhealthy',
          responseTime,
          error: 'Network error - API not responding',
          endpoint: test.testEndpoint,
          lastChecked: new Date()
        };
      }
    }

    // Other errors
    return {
      service: test.service,
      displayName: test.displayName,
      status: 'unhealthy',
      responseTime,
      error: errorMessage,
      endpoint: test.testEndpoint,
      lastChecked: new Date()
    };
  }
}

/**
 * Check all microservices using enhanced health checks
 */
export async function checkAllServicesEnhanced(): Promise<EnhancedHealthReport> {
  console.log('Starting enhanced health check for all microservices...');
  
  // Check all services in parallel
  const healthChecks = await Promise.all(
    SERVICE_TESTS.map(test => checkServiceHealth(test))
  );
  
  // Count statuses
  const healthy = healthChecks.filter(s => s.status === 'healthy').length;
  const unhealthy = healthChecks.filter(s => s.status === 'unhealthy').length;
  const unknown = healthChecks.filter(s => s.status === 'unknown').length;
  const corsErrors = healthChecks.filter(s => s.status === 'cors-error').length;
  
  const report: EnhancedHealthReport = {
    timestamp: new Date(),
    services: healthChecks,
    summary: {
      total: healthChecks.length,
      healthy,
      unhealthy,
      unknown,
      corsErrors
    }
  };
  
  console.log('Enhanced health check complete:', {
    healthy,
    unhealthy,
    unknown,
    total: healthChecks.length
  });
  
  return report;
}

/**
 * Test if Lambda URLs are configured correctly by checking CORS preflight
 */
export async function testLambdaCORS(): Promise<void> {
  console.log('Testing Lambda URL CORS configuration...');

  for (const test of SERVICE_TESTS.slice(0, 5)) { // Test first 5 services
    const healthEndpoint = HEALTH_ENDPOINTS[test.service as keyof typeof HEALTH_ENDPOINTS];
    if (!healthEndpoint) {
      console.log(`No health endpoint configured for ${test.service}`);
      continue;
    }

    console.log(`Testing ${test.displayName} at ${healthEndpoint}`);

    try {
      const response = await fetch(healthEndpoint, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'GET',
          'Access-Control-Request-Headers': 'authorization,content-type'
        }
      });

      console.log(`${test.displayName} OPTIONS response:`, {
        status: response.status,
        headers: {
          'access-control-allow-origin': response.headers.get('access-control-allow-origin'),
          'access-control-allow-methods': response.headers.get('access-control-allow-methods'),
          'access-control-allow-headers': response.headers.get('access-control-allow-headers')
        }
      });
    } catch (error) {
      console.error(`${test.displayName} OPTIONS request failed:`, error);
    }
  }
}